// One-shot registration of every source + layer the main map needs. Idempotent
// — guarded by `getSource` so it's safe to re-run after a style.load (which is
// when Mapbox tears the layer stack down). The actual visibility/opacity per
// layer is owned by `applyLayerState`; this module just makes sure the layers
// exist so it has something to drive.

import type mapboxgl from "mapbox-gl";
import {
  LAYER_SOURCES,
  LULC_COLORS,
  LULC_CHANGE_COLORS,
  CROP_COLORS,
  FOREST_COLORS,
  LULC_RASTER_TILES,
  LULC_RASTER_MIN_ZOOM,
  LULC_RASTER_MAX_ZOOM,
  SENTINEL_RASTER_TILES,
  SENTINEL_RASTER_MIN_ZOOM,
  SENTINEL_VARIANT_MAX_ZOOM,
  CROPS_RASTER_TILES,
  CROPS_RASTER_MIN_ZOOM,
  CROPS_RASTER_MAX_ZOOM,
  HARVEST_RASTER_TILES,
  HARVEST_RASTER_MIN_ZOOM,
  HARVEST_RASTER_MAX_ZOOM,
  FOREST_RASTER_TILES,
  FOREST_RASTER_MIN_ZOOM,
  FOREST_RASTER_MAX_ZOOM,
} from "../config/agricultural-lulc";
import { EMPTY_FC, fetchSource, getCachedFc } from "../utils/geojson-cache";
import { buildClassColorExpr } from "./map-helpers";

// Resolve a GeoJSON source's initial data: cache hit if we already have it
// (e.g. a re-registration after a basemap style.load), otherwise an empty
// FeatureCollection that the page swaps in via `setData` once the fetch
// completes. Lets the rest of the demo render immediately on first visit
// without blocking on ~10 MB of polygon data.
function initialDataFor(url: string): GeoJSON.FeatureCollection {
  return getCachedFc(url) ?? EMPTY_FC;
}

// Register every source + layer the main map needs, using cached GeoJSON
// where available and EMPTY_FC placeholders elsewhere. The page calls
// `loadGeoJSONIntoSource` to stream the real data in once each layer becomes
// relevant (or is already pre-fetched in the background).
export function registerMainMapLayers(m: mapboxgl.Map): void {
  registerLgaBoundary(m, initialDataFor(LAYER_SOURCES.lgaBoundary));
  // Sentinel is base imagery — register it first so every thematic raster
  // (LULC, crops, harvest, forest) paints on top of it. Mapbox draws layers
  // in registration order, so anything added later renders above.
  registerSentinelRasterPyramid(m);
  registerLulcGeoJSON(m, "lulc-2024", initialDataFor(LAYER_SOURCES.lulc2024));
  registerLulcGeoJSON(m, "lulc-2025", initialDataFor(LAYER_SOURCES.lulc2025));
  registerLulcRasterPyramid(m);
  registerLulcChange(m, initialDataFor(LAYER_SOURCES.lulcChange));
  registerLulcChangePatches(m, initialDataFor(LAYER_SOURCES.lulcChangePatches));
  registerCropsParcels(m, initialDataFor(LAYER_SOURCES.crops2025));
  registerCropsRaster(m);
  registerHarvestRaster(m);
  registerForestRaster(m);
  registerForestParcels(m, initialDataFor(LAYER_SOURCES.forest2025));
  registerParcels(m, initialDataFor(LAYER_SOURCES.parcels));
}

// Map of source URL → Mapbox source id, so the page can address sources by
// the same URL it uses with `fetchSource`/`getCachedFc`.
const SOURCE_ID_BY_URL: Record<string, string> = {
  [LAYER_SOURCES.lgaBoundary]: "lga-boundary",
  [LAYER_SOURCES.lulc2024]: "lulc-2024",
  [LAYER_SOURCES.lulc2025]: "lulc-2025",
  [LAYER_SOURCES.lulcChange]: "lulc-change",
  [LAYER_SOURCES.lulcChangePatches]: "lulc-change-patches",
  [LAYER_SOURCES.crops2025]: "crops-2025",
  [LAYER_SOURCES.forest2025]: "forest-2025",
  [LAYER_SOURCES.parcels]: "parcels",
};

// Fetch a GeoJSON URL and push the result into its already-registered
// Mapbox source. Idempotent: subsequent calls with the same URL hit the
// module-level cache and call `setData` again (cheap — Mapbox dedups equal
// references). Safe to call before `style.load` resolves; the source check
// skips the update silently and the next caller will set it.
export async function loadGeoJSONIntoSource(
  m: mapboxgl.Map,
  url: string
): Promise<void> {
  const sourceId = SOURCE_ID_BY_URL[url];
  if (!sourceId) return;
  const data = await fetchSource(url);
  const source = m.getSource(sourceId);
  if (source && "setData" in source) {
    (source as mapboxgl.GeoJSONSource).setData(data);
  }
}

function registerLgaBoundary(m: mapboxgl.Map, data: GeoJSON.FeatureCollection) {
  if (m.getSource("lga-boundary")) return;
  m.addSource("lga-boundary", { type: "geojson", data });
  m.addLayer({
    id: "lga-boundary-fill",
    type: "fill",
    source: "lga-boundary",
    paint: { "fill-color": "#6366f1", "fill-opacity": 0.05 },
  });
  m.addLayer({
    id: "lga-boundary-line",
    type: "line",
    source: "lga-boundary",
    paint: {
      "line-color": "#6366f1",
      "line-width": 2.5,
      "line-dasharray": [3, 2],
    },
  });
}

function registerLulcGeoJSON(
  m: mapboxgl.Map,
  sourceId: "lulc-2024" | "lulc-2025",
  data: GeoJSON.FeatureCollection
) {
  if (m.getSource(sourceId)) return;
  m.addSource(sourceId, { type: "geojson", data });
  m.addLayer({
    id: `${sourceId}-fill`,
    type: "fill",
    source: sourceId,
    paint: {
      "fill-color": buildClassColorExpr(LULC_COLORS),
      "fill-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

// LULC raster tiles (10 m pixel-level: 2024, 2025, and the year-over-year
// change raster). Used by the LULC wizard step in place of the parcel-
// aggregated GeoJSON above, and by step 7 (comparison) on both sides.
function registerLulcRasterPyramid(m: mapboxgl.Map) {
  const variants = ["2024", "2025", "change"] as const;
  for (const variant of variants) {
    const sourceId = `lulc-raster-${variant}`;
    if (m.getSource(sourceId)) continue;
    m.addSource(sourceId, {
      type: "raster",
      tiles: [LULC_RASTER_TILES[variant]],
      tileSize: 256,
      minzoom: LULC_RASTER_MIN_ZOOM,
      maxzoom: LULC_RASTER_MAX_ZOOM,
    });
    m.addLayer({
      id: `${sourceId}-fill`,
      type: "raster",
      source: sourceId,
      paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
      layout: { visibility: "none" },
    });
  }
}

// Sentinel-2 RGBA mosaic per year, served as XYZ WebP raster tiles. Two
// variants exist side by side ("default" z8-13 from public/, "hires" z8-15
// from S3) so we can A/B them at runtime. Powers the "Satellite Imagery"
// wizard step (step 2).
function registerSentinelRasterPyramid(m: mapboxgl.Map) {
  const years = ["2024", "2025"] as const;
  const variants = ["default", "hires"] as const;
  for (const variant of variants) {
    for (const year of years) {
      const sourceId = `sentinel-${variant}-${year}`;
      if (m.getSource(sourceId)) continue;
      m.addSource(sourceId, {
        type: "raster",
        tiles: [SENTINEL_RASTER_TILES[variant][year]],
        tileSize: 256,
        minzoom: SENTINEL_RASTER_MIN_ZOOM,
        maxzoom: SENTINEL_VARIANT_MAX_ZOOM[variant],
      });
      m.addLayer({
        id: `${sourceId}-raster`,
        type: "raster",
        source: sourceId,
        paint: { "raster-opacity": 0 },
        layout: { visibility: "none" },
      });
    }
  }
}

function registerLulcChange(m: mapboxgl.Map, data: GeoJSON.FeatureCollection) {
  if (m.getSource("lulc-change")) return;
  m.addSource("lulc-change", { type: "geojson", data });
  m.addLayer({
    id: "lulc-change-fill",
    type: "fill",
    source: "lulc-change",
    filter: ["!=", "dominant_class", "No Change"],
    paint: {
      "fill-color": buildClassColorExpr(LULC_CHANGE_COLORS),
      "fill-opacity": 0,
    },
    layout: { visibility: "none" },
  });
  m.addLayer({
    id: "lulc-change-outline",
    type: "line",
    source: "lulc-change",
    paint: {
      "line-color": "#94a3b8",
      "line-width": 0.5,
      "line-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

// Raster-derived area polygons — only meaningful as an overlay when the
// parcel-aggregated change layer is visible.
function registerLulcChangePatches(m: mapboxgl.Map, data: GeoJSON.FeatureCollection) {
  if (m.getSource("lulc-change-patches")) return;
  m.addSource("lulc-change-patches", { type: "geojson", data });
  m.addLayer({
    id: "lulc-change-patches-fill",
    type: "fill",
    source: "lulc-change-patches",
    paint: {
      "fill-color": buildClassColorExpr(LULC_CHANGE_COLORS),
      "fill-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

// Winter 2025 parcel-aggregated crops (Wheat/Barley/Canola). ~5,400 of 6,200
// parcels have no crop and come through with dominant_class=null; filtered
// out so they don't drown the map in fallback grey.
function registerCropsParcels(m: mapboxgl.Map, data: GeoJSON.FeatureCollection) {
  if (m.getSource("crops-2025")) return;
  m.addSource("crops-2025", { type: "geojson", data });
  m.addLayer({
    id: "crops-2025-fill",
    type: "fill",
    source: "crops-2025",
    filter: ["in", ["get", "dominant_class"], ["literal", ["Wheat", "Canola", "Barley"]]],
    paint: {
      "fill-color": buildClassColorExpr(CROP_COLORS),
      "fill-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

// Summer 2024 pixel-level raster (Sorghum/Cotton/Other Crop/Fallow).
// Different season from the parcel layer above — the per-pixel source only
// exists for summer.
function registerCropsRaster(m: mapboxgl.Map) {
  if (m.getSource("crops-raster-summer2024")) return;
  m.addSource("crops-raster-summer2024", {
    type: "raster",
    tiles: [CROPS_RASTER_TILES],
    tileSize: 256,
    minzoom: CROPS_RASTER_MIN_ZOOM,
    maxzoom: CROPS_RASTER_MAX_ZOOM,
  });
  m.addLayer({
    id: "crops-raster-summer2024-fill",
    type: "raster",
    source: "crops-raster-summer2024",
    paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
    layout: { visibility: "none" },
  });
}

// Same source AOI as crops, but each cropped class splits into harvested vs
// standing using harvest_2_<class>.tif from the same delivery.
function registerHarvestRaster(m: mapboxgl.Map) {
  if (m.getSource("harvest-raster-summer2024")) return;
  m.addSource("harvest-raster-summer2024", {
    type: "raster",
    tiles: [HARVEST_RASTER_TILES],
    tileSize: 256,
    minzoom: HARVEST_RASTER_MIN_ZOOM,
    maxzoom: HARVEST_RASTER_MAX_ZOOM,
  });
  m.addLayer({
    id: "harvest-raster-summer2024-fill",
    type: "raster",
    source: "harvest-raster-summer2024",
    paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
    layout: { visibility: "none" },
  });
}

// 2024 pixel-level forest raster (Pipeline B forest.tif). Toggled alongside
// the parcel-aggregated GeoJSON via layers.forest.useRaster.
function registerForestRaster(m: mapboxgl.Map) {
  if (m.getSource("forest-raster-2024")) return;
  m.addSource("forest-raster-2024", {
    type: "raster",
    tiles: [FOREST_RASTER_TILES],
    tileSize: 256,
    minzoom: FOREST_RASTER_MIN_ZOOM,
    maxzoom: FOREST_RASTER_MAX_ZOOM,
  });
  m.addLayer({
    id: "forest-raster-2024-fill",
    type: "raster",
    source: "forest-raster-2024",
    paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
    layout: { visibility: "none" },
  });
}

function registerForestParcels(m: mapboxgl.Map, data: GeoJSON.FeatureCollection) {
  if (m.getSource("forest-2025")) return;
  m.addSource("forest-2025", { type: "geojson", data });
  m.addLayer({
    id: "forest-2025-fill",
    type: "fill",
    source: "forest-2025",
    paint: {
      "fill-color": buildClassColorExpr(FOREST_COLORS),
      "fill-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

function registerParcels(m: mapboxgl.Map, data: GeoJSON.FeatureCollection) {
  if (m.getSource("parcels")) return;
  m.addSource("parcels", { type: "geojson", data });
  m.addLayer({
    id: "parcels-fill",
    type: "fill",
    source: "parcels",
    // Click hit-detection layer — invisible but queryable.
    paint: { "fill-opacity": 0 },
    layout: { visibility: "none" },
  });
  m.addLayer({
    id: "parcels-line",
    type: "line",
    source: "parcels",
    paint: {
      "line-color": "#a78bfa",
      "line-width": 1.5,
      "line-opacity": 0,
    },
    layout: { visibility: "none" },
  });
  m.addLayer({
    id: "parcels-highlight",
    type: "line",
    source: "parcels",
    paint: {
      "line-color": "#facc15",
      "line-width": 3,
      "line-opacity": 0,
    },
    filter: ["==", "cadastre_polygon_pid", ""],
    layout: { visibility: "visible" },
  });
}
